/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {RadioGroup} from "@headlessui/react";
import clsx from "clsx";
import {CheckIcon, ExclamationTriangleIcon} from "@heroicons/react/24/solid";
import {PackagesItemsPerson} from "types/entities/PackagesItems";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";
import {useTypedSelector} from "helpers/reducers";
import dayjs from "dayjs";

interface BookingPackageItemsDurationsProps {
    chosenDuration: number | undefined;
    setChosenDuration: (val: number) => void;
    enabled: boolean;
    withKarting?: number;
    className?: string;
    personType: PackageItemPersonType;
    disabled2Hours: boolean;
    disabled1Hour: boolean;
    isSeller?: boolean;
}

const BookingPackageItemsDurations = (props: BookingPackageItemsDurationsProps) => {
    const {t} = useTranslation();
    const [chosenDuration, setChosenDuration] = useState<number | undefined>(undefined);
    const {getPackageItemForCombination} = usePackageItemKartingOptions();
    const hallOpeningHours = useHallOpeningHours();
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const timeslotDate = selectedTimeslot?.start_at ? dayjs(selectedTimeslot.start_at) : null;

    const durationOptions = props.disabled2Hours
        ? [
            {name: 3 + " " + t("hours"), enabled: true, value: 180},
            {name: 4 + " " + t("hours"), enabled: true, value: 240},
            {name: 4.5 + " " + t("hours"), enabled: true, value: 270},
            {name: 6 + " " + t("hours"), enabled: true, value: 360},
        ]
        : props.disabled1Hour
            ? [
                {name: 2 + " " + t("hours"), enabled: true, value: 120},
                {name: 3 + " " + t("hours"), enabled: true, value: 180},
                {name: 4 + " " + t("hours"), enabled: true, value: 240},
                {name: 4.5 + " " + t("hours"), enabled: true, value: 270},
                {name: 6 + " " + t("hours"), enabled: true, value: 360},
            ]
            : [
                {name: 1 + " " + t("hour"), enabled: true, value: 60},
                {name: 2 + " " + t("hours"), enabled: true, value: 120},
                {name: 3 + " " + t("hours"), enabled: true, value: 180},
                {name: 4 + " " + t("hours"), enabled: true, value: 240},
                {name: 4.5 + " " + t("hours"), enabled: true, value: 270},
                {name: 6 + " " + t("hours"), enabled: true, value: 360},
            ];

    const isPackageItemDisabledForCombination = duration => {
        if (!props.enabled) {
            return true;
        }
        const packagesItemsPerson: PackagesItemsPerson | undefined = getPackageItemForCombination(duration, !!props.withKarting, props.personType);
        return packagesItemsPerson === undefined;
    };

    const setChosenDurationHandler = (option: number) => {
        // setChosenDuration(option);
        props.setChosenDuration(option);
    };

    // useEffect(() => {
    //     const isDisabledFromPackageItem = isPackageItemDisabledForCombination(props.chosenDuration);
    //     setDisabled(isDisabledFromPackageItem);
    // }, [props.chosenDuration]);

    useEffect(() => {
        setChosenDuration(props?.chosenDuration ? props.chosenDuration : undefined);
    }, [props.chosenDuration]);

    const colorClasses = {
        "red-bg": "bg-arbo-red",
        "yellow-bg": "bg-arbo-yellow",
        "red-all": "bg-arbo-red border-arbo-red",
        "yellow-all": "bg-arbo-yellow border-arbo-yellow",
        "black-all": "bg-gray-900 border-gray-900",
        "black-bg": "bg-gray-900",
        "top-black":
            "hover:bg-gray-900 data-[checked]:bg-white data-[checked]:ring-0 data-[checked]:border-gray-900 data-[checked]:ring-gray-900 data-[checked]:text-gray-900",
        "top-red":
            "hover:arbo-red data-[checked]:bg-white data-[checked]:ring-0 data-[checked]:border-arbo-red data-[checked]:ring-arbo-red data-[checked]:text-arbo-red",
        "top-yellow":
            "hover:arbo-yellow data-[checked]:bg-white data-[checked]:ring-0 data-[checked]:border-arbo-yellow data-[checked]:ring-arbo-yellow data-[checked]:text-arbo-yellow",
    };

    const colorClass = props.withKarting == 1 ? "red" : "yellow";

    const UnavailableTime = () => {
        return (
            <div
                className={
                    "w-full overflow-hidden lg:w-auto max-w-[260px] sm:max-w-[160px] min-h-[40px] sm:min-h-[46px] xl:min-h-[50px] h-full relative flex flex-row items-center justify-center rounded-md text-gray-600 border-2 border-gray-300 bg-transparent shadow-sm"
                }
            >
                <span
                    className={"z-20 px-1 text-sm bg-white/50 text-center leading-1 shrink"}>{t("Outside opening hours")}</span>
                <aside
                    className={"absolute z-10 w-full transform-gpu -rotate-[7deg] sm:-rotate-12 h-px left-0 bottom-1/2 bg-red-400"} />
            </div>
        );
    };
    return (
        <div className={clsx("BookingPackageItemsDurations flex w-full", props.className)}>
            <fieldset aria-label={t("Choose a duration of your stay")} className={"w-full sm:w-auto"}>
                <RadioGroup
                    className="flex flex-col items-center space-y-2 sm:space-y-0 w-full sm:w-auto sm:grid sm:grid-cols-3 sm:gap-2 xl:gap-3"
                    defaultValue={chosenDuration}
                    value={chosenDuration}
                    onChange={setChosenDurationHandler}
                >
                    {durationOptions.map(option => {
                        const isDisabledFromPackageItem = isPackageItemDisabledForCombination(option.value);
                        const showWarning: boolean = props.isSeller == undefined ? false : hallOpeningHours.packageHasEndedAfterHallOpeningHours(timeslotDate, option.value / 60);

                        if (isDisabledFromPackageItem) {
                            return <React.Fragment key={option.value}><></></React.Fragment>;
                            // return <UnavailableTime key={option.name} />;
                        }
                        return (
                            <RadioGroup.Option
                                key={option.value}
                                className={clsx(
                                    props.enabled && option.enabled ? "cursor-pointer focus:outline-none" : "cursor-not-allowed opacity-25",
                                    "w-full lg:w-auto max-w-[260px] sm:max-w-full sm:min-h-[46px] xl:min-h-[50px] h-full relative flex flex-row items-center justify-center rounded-md pb-0 font-medium text-gray-600 border-2 border-gray-300 bg-transparent shadow-sm duration-150 transition-all ring-1 ring-transparent delay-0",
                                    colorClass === "yellow" ? colorClasses["top-yellow"] : colorClasses["top-red"],

                                    // " hover:arbo-yellow data-[checked]:bg-white data-[checked]:ring-0 data-[checked]:border-arbo-yellow data-[checked]:ring-arbo-yellow data-[checked]:text-arbo-yellow"
                                )}
                                disabled={isDisabledFromPackageItem || !props.enabled || !option.enabled}
                                value={option.value}
                                {...(chosenDuration == option.value ? {"data-checked": true} : {})}
                            >
                                {({checked}) => (
                                    <div
                                        className={clsx(
                                            "relative overflow-hidden break-none flex flex-row justify-between items-center w-full h-full",
                                            checked
                                                ? colorClass === "yellow"
                                                    ? colorClasses["yellow-bg"]
                                                    : colorClasses["red-bg"]
                                                : isDisabledFromPackageItem
                                                    ? "!bg-red-500"
                                                    : "bg-gray-300",
                                        )}
                                    >
                                        <div
                                            className={clsx(
                                                "px-2.5 py-2 xl:px-2.5 xl:py-3 border-r h-full flex items-center justify-center",
                                                checked
                                                    ? colorClass === "yellow"
                                                        ? colorClasses["yellow-all"]
                                                        : colorClasses["red-all"]
                                                    : "bg-gray-300 border-gray-300",
                                            )}
                                        >
                                            <div
                                                className={clsx(
                                                    "w-4 h-4 xl:w-5 xl:h-5 border flex justify-center items-center rounded-full",
                                                    checked
                                                        ? colorClass === "yellow"
                                                            ? colorClasses["yellow-all"]
                                                            : colorClasses["red-all"]
                                                        : "border-gray-300 bg-white",
                                                )}
                                            >
                                                {checked && <CheckIcon className={"w-5 h-5 flex-none text-white"} />}
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                "px-2 py-2 xl:px-3 2xl:px-4 xl:py-3 text-sm xl:text-base h-full w-full rounded-md flex items-center justify-center bg-white"
                                            }
                                        >
                                            <span>{option.name}</span>
                                            {showWarning && (
                                                <ExclamationTriangleIcon
                                                    className={"size-5 flex-none text-red-500 ml-1"} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </RadioGroup.Option>
                        );
                    })}
                </RadioGroup>
            </fieldset>
        </div>
    );
};

export default BookingPackageItemsDurations;
